/*
 * The inbox's eight labels (`docs/2026-10-16-master-inbox-ai-labels.md`, sections 2 and 3) and where a
 * labelled conversation sits in inbox v2's list (`docs/2026-10-16-inbox-v2-build-plan.md`, card 4).
 *
 * A label is what the lead's latest human reply said, in a small vocabulary a client's reps learn
 * once. It comes from the classification the reply agent already makes, mapped here by a pure rule,
 * so one model call serves both: the agent's 16 stances, 18 asks and 8 routes change with drafting
 * rules, and these do not. A rep's relabel is stored beside it and never feeds the classifier.
 *
 * Deterministic and total: every input has exactly one result, decided by the first rule that matches,
 * in the doc's order.
 */
import type { OutsideGoalKind } from "../../../skills/leadcue-reply/scripts/lib/outside-goal.ts";

export const INBOX_LABELS = [
  "interested",
  "question",
  "not_now",
  "wrong_person",
  "not_interested",
  "asked_to_stop",
  "auto_reply",
  "not_a_sales_reply",
] as const;
export type InboxLabelCode = (typeof INBOX_LABELS)[number];

/** What the classification said about one reply, as it is stored (`reply.classified`). */
export interface LabelClassification {
  readonly stance: string;
  readonly asks: readonly string[];
  readonly route: string | null;
}

export type InboxLabel =
  | { readonly label: "wrong_person"; readonly namesSomeone: boolean }
  | { readonly label: "auto_reply"; readonly outOfOffice: boolean }
  | {
      readonly label: "not_a_sales_reply";
      readonly kind: Exclude<OutsideGoalKind, "wrong_person"> | null;
    }
  | {
      readonly label: Exclude<InboxLabelCode, "wrong_person" | "auto_reply" | "not_a_sales_reply">;
    }
  /** Nothing the lead said fits a label: the classifier said `other`, or there is no classification. */
  | { readonly label: null; readonly adminReview: false }
  /** The seller's own message filed as the lead's: no label, and only the agency is shown it (L4). */
  | { readonly label: null; readonly adminReview: true };

const STOP_STANCES = new Set(["opt_out_request", "legal_privacy_demand"]);
const NOT_A_SALES_REPLY_STANCES = new Set([
  "unrelated_correspondence",
  "reverse_pitch_partnership",
]);
const NAMED_ROUTES = new Set([
  "referral_named_with_address",
  "referral_named_no_address",
  "role_change_left_to_new_icp_company",
]);
const WRONG_PERSON_ROUTES = new Set([
  "wrong_person_unnamed",
  "gatekeeper_response",
  "role_change_left_company",
  "role_change_internal_remit",
]);
const NO_STANCES = new Set(["not_interested_soft", "not_interested_hard", "hostile_complaint"]);
const QUESTION_STANCES = new Set(["curiosity_no_intent", "brushoff_soft_yes"]);
const NEGATIVE_STANCES = new Set([...NO_STANCES, ...STOP_STANCES, "timing_defer"]);
const INFORMATIONAL_ASKS = new Set([
  "send_materials",
  "answer_pricing",
  "confirm_scope",
  "answer_technical",
  "provide_references",
  "answer_data_source",
  "sign_nda",
  "route_to_procurement",
]);

/** What the rules read: the classification flattened, plus the two host-side signals. */
interface LabelFacts {
  readonly stance: string | null;
  readonly asks: readonly string[];
  readonly route: string | null;
  readonly outsideGoal: OutsideGoalKind | null;
  readonly suppressed: boolean;
}

type LabelRule = (facts: LabelFacts) => InboxLabel | null;

/**
 * The mapping table, one rule per row, in the doc's order: the first rule that answers decides.
 * 1. A stop, in any form, before anything else: a missed one invites a reply to someone who asked
 *    not to be written to.
 */
const LABEL_RULES: readonly LabelRule[] = [
  ({ suppressed, stance, asks }) =>
    suppressed || (stance !== null && STOP_STANCES.has(stance)) || asks.includes("remove_me")
      ? { label: "asked_to_stop" }
      : null,
  // 2. Automatic replies.
  ({ stance }) =>
    stance === "auto_reply_ooo" || stance === "auto_reply_other"
      ? { label: "auto_reply", outOfOffice: stance === "auto_reply_ooo" }
      : null,
  // 3. Not a sales reply: an applied outside-goal verdict other than wrong person, or a stance that
  // says the writer is not a prospect.
  ({ outsideGoal }) =>
    outsideGoal !== null && outsideGoal !== "wrong_person"
      ? { label: "not_a_sales_reply", kind: outsideGoal }
      : null,
  ({ stance }) =>
    stance !== null && NOT_A_SALES_REPLY_STANCES.has(stance)
      ? { label: "not_a_sales_reply", kind: null }
      : null,
  // 4. The seller's own message filed as inbound.
  ({ stance }) => (stance === "not_a_lead_reply" ? { label: null, adminReview: true } : null),
  // 5 and 6. Wrong person, naming someone or not.
  ({ route }) =>
    route !== null && NAMED_ROUTES.has(route)
      ? { label: "wrong_person", namesSomeone: true }
      : null,
  ({ route, stance, outsideGoal }) =>
    (route !== null && WRONG_PERSON_ROUTES.has(route)) ||
    stance === "role_change_departure_auto_notice" ||
    outsideGoal === "wrong_person"
      ? { label: "wrong_person", namesSomeone: false }
      : null,
  // Nothing classified: no label.
  ({ stance }) => (stance === null ? { label: null, adminReview: false } : null),
  // 7. A no.
  ({ stance }) => (stance !== null && NO_STANCES.has(stance) ? { label: "not_interested" } : null),
  // 8. Not now.
  ({ stance, asks }) =>
    stance === "timing_defer" || asks.includes("stop_until_date") ? { label: "not_now" } : null,
  // 9. Interested.
  ({ stance }) => (stance === "interested_engaged" ? { label: "interested" } : null),
  // 10. A question before deciding.
  ({ stance, asks }) =>
    stance !== null &&
    (QUESTION_STANCES.has(stance) ||
      (!NEGATIVE_STANCES.has(stance) && asks.some((ask) => INFORMATIONAL_ASKS.has(ask))))
      ? { label: "question" }
      : null,
];

/**
 * The label for the lead's latest human reply. `outsideGoal` is the applied outside-goal verdict, if
 * the host checked one; `suppressed` is our own suppression record for the conversation. Rule 11:
 * nothing that fits has no label.
 */
export function inboxLabel(
  classification: LabelClassification | null,
  outsideGoal: OutsideGoalKind | null,
  suppressed: boolean,
): InboxLabel {
  const facts: LabelFacts = {
    stance: classification?.stance ?? null,
    asks: classification?.asks ?? [],
    route: classification?.route ?? null,
    outsideGoal,
    suppressed,
  };
  for (const rule of LABEL_RULES) {
    const decided = rule(facts);
    if (decided) return decided;
  }
  return { label: null, adminReview: false };
}

/** Where a conversation sits in inbox v2's list. */
export type InboxPlacement = "do_first" | "newest" | "no_reply_needed";

/**
 * Do first holds Interested and Question while it is the rep's move, that is while the lead wrote
 * last. A no or a stop folds into No reply needed, with the thread one click away. Everything else,
 * auto-replies and unlabelled replies included, waits in Newest.
 */
export function inboxPlacement(
  label: InboxLabelCode | null,
  lastFrom: "lead" | "seller",
): InboxPlacement {
  if (label === "not_interested" || label === "asked_to_stop") return "no_reply_needed";
  if ((label === "interested" || label === "question") && lastFrom === "lead") return "do_first";
  return "newest";
}
